import { Card, CardContent } from '@ui/card.tsx';
import { Languages, ChevronRight, FileTextIcon, Images, Link2, Wand2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Separator } from '@ui/separator.tsx';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { useRoutes } from '@r/hooks/use-routes.ts';

export const PersonalizationPage = () => {
  const { t } = useTranslation();
  const user = useUser();
  const routes = useRoutes();
  const activeWorkspace = getActiveWorkspaceUniversal(user.data?.visibleWorkspaces ?? []);
  const roles = activeWorkspace?.roles ?? [];
  const canAccessAssets = roles.includes('ROLE_ADMIN') || roles.includes('ROLE_FIDDY_ADMIN');

  const isRouteAvailable = (path: string) =>
    routes.data?.some((route) => route.path === path) ?? false;

  const personalizationOptions = [
    ...(canAccessAssets
      ? [
          {
            title: t('personalization.assets.title'),
            icon: Images,
            url: '/personalization/assets'
          }
        ]
      : []),
    ...(isRouteAvailable('/personalization/external-links')
      ? [
          {
            title: t('personalization.external.links.title'),
            icon: Link2,
            url: '/personalization/external-links'
          }
        ]
      : []),
    ...(isRouteAvailable('/personalization/translations')
      ? [
          {
            title: t('menu.admin.translations'),
            icon: Languages,
            url: '/personalization/translations'
          }
        ]
      : []),
    ...(isRouteAvailable('/personalization/display')
      ? [
          {
            title: t('personalization.display.title'),
            icon: Wand2,
            url: '/personalization/display'
          }
        ]
      : []),
    ...(isRouteAvailable('/personalization/legal')
      ? [
          {
            title: t('admin.legal.documents.title'),
            icon: FileTextIcon,
            url: '/personalization/legal'
          }
        ]
      : [])
  ];

  return (
    <div className="flex flex-col space-y-10 grow">
      <div className="space-y-3">
        <h1 className="text-4xl font-bold">{t('menu.admin.personnalisation')}</h1>
        <p className="font-light text-muted-foreground">{t('personalization.description')}</p>
      </div>

      <Card className="w-full">
        <CardContent className="p-6">
          <h2 className="mb-6 text-xl font-semibold">{t('personalization.customize.program')}</h2>
          <div className="space-y-0">
            {personalizationOptions.map((option, index) => {
              const Icon = option.icon;
              const isLast = index === personalizationOptions.length - 1;
              return (
                <div key={option.url}>
                  <Link
                    to={option.url}
                    className="flex items-center py-4 w-full transition-colors hover:bg-muted/50"
                  >
                    <div className="flex flex-1 items-center space-x-4">
                      <Icon className="w-5 h-5 text-foreground" />
                      <span className="text-base font-medium">{option.title}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </Link>
                  {!isLast && <Separator />}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
